import { useTranslation } from 'react-i18next'
import { Check, X, Loader2 } from 'lucide-react'
import { cn } from '@/core/infra_utils'
import type { LifecycleStage, StageStatus } from './infra_lifecycle_stages'

interface Props {
  stages: LifecycleStage[]
  onStageClick?: (operationId: string) => void
  onConnectedClick?: () => void
}

const statusColors: Record<StageStatus, string> = {
  completed: 'bg-emerald-500 dark:bg-emerald-400',
  in_progress: 'bg-blue-500 dark:bg-blue-400 animate-pulse',
  failed: 'bg-red-500 dark:bg-red-400',
  not_started: 'bg-slate-300 dark:bg-slate-600',
}

const lineColors: Record<StageStatus, string> = {
  completed: 'bg-emerald-300 dark:bg-emerald-600',
  in_progress: 'bg-blue-300 dark:bg-blue-600',
  failed: 'bg-red-300 dark:bg-red-600',
  not_started: 'bg-slate-200 dark:bg-slate-700',
}

const labelKeys: Record<string, string> = {
  created: 'pages:environments.lifecycle.created',
  infrastructure: 'pages:environments.lifecycle.infrastructure',
  runtime: 'pages:environments.lifecycle.runtime',
  connected: 'pages:environments.lifecycle.connected',
}

function StageIcon({ status }: { status: StageStatus }) {
  if (status === 'completed') return <Check size={12} className="text-white" />
  if (status === 'failed') return <X size={12} className="text-white" />
  if (status === 'in_progress') return <Loader2 size={12} className="text-white animate-spin" />
  return null
}

export function ProvisioningLifecycle({ stages, onStageClick, onConnectedClick }: Props) {
  const { t } = useTranslation(['pages'])

  const handleClick = (stage: LifecycleStage) => {
    if (stage.id === 'connected') {
      onConnectedClick?.()
    } else if (stage.operationId) {
      onStageClick?.(stage.operationId)
    }
  }

  const isClickable = (stage: LifecycleStage) =>
    stage.id === 'connected' || !!stage.operationId

  return (
    <div className="flex items-center gap-0 w-full">
      {stages.map((stage, i) => (
        <div key={stage.id} className="flex items-center flex-1 last:flex-none">
          <div
            data-stage={stage.id}
            data-status={stage.status}
            role={isClickable(stage) ? 'button' : undefined}
            tabIndex={isClickable(stage) ? 0 : undefined}
            className={cn(
              'flex flex-col items-center gap-1 min-w-[70px]',
              isClickable(stage) && 'cursor-pointer',
            )}
            onClick={() => handleClick(stage)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(stage) }}
          >
            <div className={cn('w-6 h-6 rounded-full flex items-center justify-center', statusColors[stage.status])}>
              <StageIcon status={stage.status} />
            </div>
            <span className="text-xs font-medium text-slate-700 dark:text-slate-300 text-center">
              {t(labelKeys[stage.id] ?? stage.id)}
            </span>
            {stage.summary && (
              <span className="text-[10px] text-slate-500 dark:text-slate-400">{stage.summary}</span>
            )}
          </div>
          {i < stages.length - 1 && stages[i + 1] && (
            <div className={cn('h-0.5 flex-1 mx-1', lineColors[stages[i + 1]!.status])} />
          )}
        </div>
      ))}
    </div>
  )
}
